import React, { useState } from 'react';
import { useJobs } from '../../context/JobPollerContext';
import {
    Box, Paper, Typography, IconButton,
    LinearProgress, Chip, Collapse,
} from '@mui/material';
import {
    ExpandLess, ExpandMore, CloudUpload,
    CheckCircle, Error as ErrorIcon, HourglassEmpty,
    Speed,
} from '@mui/icons-material';

const STAGE_LABELS = {
    queued: 'Queued',
    s3_upload: 'Uploading to cloud',
    text_extraction: 'Extracting text',
    shadow_file: 'Creating search index',
    kb_ingestion: 'KB ingestion',
    properties: 'Extracting properties',
    indexing: 'Indexing for search',
    loading_data: 'Loading data',
    analyzing: 'AI analyzing',
    ai_analyzing: 'AI analyzing (may take 1-2 min)',
    generating: 'Generating',
    loading_drawing: 'Loading drawing data',
    loading_requirements: 'Loading requirements',
    extracting: 'Extracting',
    saving: 'Saving results',
    done: 'Complete',
};

const JOB_TYPE_LABELS = {
    file_extraction: 'File Upload',
    kb_extraction: 'KB Upload',
    compliance_check: 'Compliance Check',
    bom_extraction: 'BOM Extraction',
    document_analysis: 'Document Analysis',
    report_generation: 'Report Generation',
    generate_description: 'AI Description',
    requirements_extraction: 'Requirements Extraction',
    ai_health_summary: 'AI Health Summary',
    ai_generate_tests: 'AI Test Generation',
    ai_inspection_checklist: 'AI Inspection Checklist',
    ai_verification_matrix: 'AI Verification Matrix',
    ai_failure_analysis: 'AI Failure Analysis',
};

const JOB_TYPE_COLORS = {
    compliance_check: '#e65100',
    bom_extraction: '#0288d1',
    document_analysis: '#2e7d32',
    file_extraction: '#667eea',
    kb_extraction: '#764ba2',
    report_generation: '#9c27b0',
    generate_description: '#00897b',
    requirements_extraction: '#ef6c00',
    ai_health_summary: '#558b2f',
    ai_generate_tests: '#667eea',
    ai_inspection_checklist: '#7e57c2',
    ai_verification_matrix: '#00897b',
    ai_failure_analysis: '#d32f2f',
};


const BottomActivityPanel = ({ projectId }) => {
    const [expanded, setExpanded] = useState(false);

    // Jobs from shared poller — no local polling
    const { jobs, activeCount: jobCount } = useJobs();

    return (
        <Paper
            elevation={0}
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                overflow: 'hidden',
                width: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderBottom: 'none',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    py: 0.75,
                    px: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Speed sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                        Command Center
                    </Typography>
                    {jobCount > 0 && (
                        <Chip
                            label={`${jobCount} processing`}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 500,
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.6 },
                                },
                            }}
                        />
                    )}
                </Box>
                <IconButton size="small" sx={{ color: 'white', width: 28, height: 28 }}>
                    {expanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <ExpandLess sx={{ fontSize: 18 }} />}
                </IconButton>
            </Box>

            {/* Content */}
            <Collapse in={expanded}>
                <Box sx={{ height: 260, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
                    {/* Processing Queue */}
                    {(
                        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>
                            {!projectId ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        Select a project to see processing jobs.
                                    </Typography>
                                </Box>
                            ) : jobs.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                                    <CheckCircle sx={{ fontSize: 32, color: '#2e7d32', opacity: 0.5 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        All clear — no active processing jobs.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {jobs.map(job => {
                                        const typeColor = JOB_TYPE_COLORS[job.job_type] || '#667eea';
                                        const typeLabel = JOB_TYPE_LABELS[job.job_type] || job.job_type;
                                        const isDone = job.status === 'completed';
                                        const isFailed = job.status === 'failed';
                                        return (
                                        <Box
                                            key={job.job_id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: isDone ? 'success.light' : isFailed ? 'error.light' : 'divider',
                                                bgcolor: isDone ? 'rgba(46, 125, 50, 0.04)' : isFailed ? 'rgba(198, 40, 40, 0.04)' : 'background.paper',
                                                opacity: isDone ? 0.75 : 1,
                                            }}
                                        >
                                            {isDone && <CheckCircle sx={{ fontSize: 18, color: '#2e7d32' }} />}
                                            {isFailed && <ErrorIcon sx={{ fontSize: 18, color: '#c62828' }} />}
                                            {job.status === 'processing' && <CloudUpload sx={{ fontSize: 18, color: typeColor }} />}
                                            {job.status === 'queued' && <HourglassEmpty sx={{ fontSize: 18, color: '#757575' }} />}

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                                    <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                                        {job.file_name}
                                                    </Typography>
                                                    <Chip
                                                        label={typeLabel}
                                                        size="small"
                                                        sx={{
                                                            height: 16, fontSize: '0.6rem', fontWeight: 600,
                                                            bgcolor: `${typeColor}15`, color: typeColor,
                                                        }}
                                                    />
                                                </Box>
                                                {!isDone && !isFailed && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            variant={job.status === 'queued' ? 'indeterminate' : 'determinate'}
                                                            value={(job.progress || 0) * 100}
                                                            sx={{
                                                                flex: 1, height: 3, borderRadius: 1,
                                                                '& .MuiLinearProgress-bar': { bgcolor: typeColor },
                                                            }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                            {STAGE_LABELS[job.stage] || job.stage || 'Queued'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {isDone && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#2e7d32' }}>Completed</Typography>
                                                )}
                                                {isFailed && (
                                                    <Typography variant="caption" noWrap sx={{ fontSize: '0.65rem', color: '#c62828' }}>
                                                        {job.error_message || 'Failed'}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {!isDone && !isFailed && (
                                                <Chip
                                                    label={`${Math.round((job.progress || 0) * 100)}%`}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.65rem', minWidth: 40 }}
                                                />
                                            )}
                                        </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    )}

                </Box>
            </Collapse>
        </Paper>
    );
};

export default BottomActivityPanel;
